'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { FileSearch, Plus, Search, Download, Eye, X, ChevronRight, Calendar, Users, Package, Clock, AlertTriangle, Send, Building2, MapPin, CreditCard, Truck, FileText, Filter,  } from 'lucide-react';
import { MOCK_RFQS, MOCK_REQUISITIONS, SUPPLIERS, RFQ_STATUS_STYLES, PROCUREMENT_CATEGORIES, UNITS, formatUGX, RFQ, RFQStatus, RFQItem, RFQSupplier,  } from '../../components/procurementData';
import { useRole } from '@/lib/useRole';

// ─── Status Badge ─────────────────────────────────────────────────────────────
function RFQStatusBadge({ status }: { status: RFQStatus }) {
  const st = RFQ_STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
      {st.label}
    </span>
  );
}

// ─── Supplier Response Badge ──────────────────────────────────────────────────
function ResponseBadge({ status }: { status: RFQSupplier['responseStatus'] }) {
  const map = {
    pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700' },
    responded: { label: 'Responded', cls: 'bg-green-50 text-green-700' },
    declined: { label: 'Declined', cls: 'bg-red-50 text-red-700' },
    no_response: { label: 'No Response', cls: 'bg-gray-100 text-gray-500' },
  };
  const s = map[status];
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

// ─── RFQ Detail Drawer ────────────────────────────────────────────────────────
function RFQDrawer({ rfq, onClose, onIssue, onClose: onCloseRFQ }: {
  rfq: RFQ;
  onClose: () => void;
  onIssue: (id: string) => void;
}) {
  const { can } = useRole();
  const canManage = can('manage_procurement');
  const respondedCount = rfq.suppliers.filter(s => s.responseStatus === 'responded').length;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-base font-bold text-gray-900">{rfq.rfqNumber}</div>
            <div className="text-xs text-gray-400 mt-0.5">{rfq.farm} · {rfq.procurementOfficer}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-5">
          {/* Status */}
          <div className="flex items-center gap-2 flex-wrap">
            <RFQStatusBadge status={rfq.status} />
            {rfq.requisitionNumber && (
              <Link href="/procurement/requisitions" className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors">
                <FileText size={10} />
                {rfq.requisitionNumber}
              </Link>
            )}
          </div>

          {/* Key Details */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'RFQ Date', value: rfq.rfqDate, icon: Calendar },
              { label: 'Submission Deadline', value: rfq.submissionDeadline, icon: Clock },
              { label: 'Currency', value: rfq.currency, icon: CreditCard },
              { label: 'Payment Terms', value: rfq.paymentTerms, icon: CreditCard },
              { label: 'Delivery Terms', value: rfq.deliveryTerms, icon: Truck },
              { label: 'Procurement Officer', value: rfq.procurementOfficer, icon: Users },
            ].map(f => (
              <div key={f.label}>
                <div className="text-xs text-gray-400 font-medium">{f.label}</div>
                <div className="text-sm font-semibold text-gray-800 mt-0.5">{f.value}</div>
              </div>
            ))}
          </div>

          {/* Delivery Location */}
          <div>
            <div className="text-xs text-gray-400 font-medium mb-1 flex items-center gap-1"><MapPin size={10} /> Delivery Location</div>
            <div className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3">{rfq.deliveryLocation}</div>
          </div>

          {/* Items */}
          <div>
            <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-1.5">
              <Package size={12} /> RFQ Items ({rfq.items.length})
            </div>
            <div className="space-y-2">
              {rfq.items.map(item => (
                <div key={item.id} className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-800">{item.description}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{item.category} · {item.unit}</div>
                      {item.specifications && <div className="text-xs text-gray-500 mt-1 italic">{item.specifications}</div>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-gray-900">{item.quantity} {item.unit}</div>
                      {item.requiredDeliveryDate && <div className="text-xs text-gray-400">By {item.requiredDeliveryDate}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Suppliers */}
          <div>
            <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide flex items-center gap-1.5">
              <Building2 size={12} /> Invited Suppliers ({rfq.suppliers.length})
            </div>
            {rfq.suppliers.length === 0 ? (
              <div className="text-sm text-gray-400 bg-gray-50 rounded-xl p-4 text-center">No suppliers invited yet</div>
            ) : (
              <div className="space-y-2">
                {rfq.suppliers.map(sup => (
                  <div key={sup.supplierId} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">{sup.supplierName}</div>
                      <div className="text-xs text-gray-400">{sup.supplierCode}{sup.sentDate ? ` · Sent ${sup.sentDate}` : ''}</div>
                    </div>
                    <ResponseBadge status={sup.responseStatus} />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 text-xs text-gray-500">
              {respondedCount} of {rfq.suppliers.length} supplier{rfq.suppliers.length !== 1 ? 's' : ''} responded
            </div>
          </div>

          {/* Notes */}
          {rfq.notes && (
            <div>
              <div className="text-xs text-gray-400 font-medium mb-1">Notes</div>
              <div className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3">{rfq.notes}</div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-4 border-t border-gray-100 flex gap-2 flex-wrap">
          {canManage && rfq.status === 'draft' && (
            <button
              onClick={() => onIssue(rfq.id)}
              className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors"
            >
              <Send size={14} />
              Issue RFQ
            </button>
          )}
          <Link
            href={`/procurement/quotations?rfq=${rfq.id}`}
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors"
          >
            <FileSearch size={14} />
            View Quotations
          </Link>
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New RFQ Form ─────────────────────────────────────────────────────────────
function NewRFQForm({ onClose, onSave }: { onClose: () => void; onSave: (rfq: RFQ) => void }) {
  const approvedReqs = MOCK_REQUISITIONS.filter(r => r.status === 'approved');

  const [form, setForm] = useState({
    requisitionId: '',
    submissionDeadline: '',
    paymentTerms: 'Net 30',
    deliveryTerms: 'DDP',
    deliveryLocation: 'Idudi Poultry Farm, Iganga Road, Idudi',
    notes: '',
    currency: 'UGX',
  });
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [items, setItems] = useState<RFQItem[]>([
    { id: 'new-1', description: '', specifications: '', category: 'Feed', unit: 'bag', quantity: 1 },
  ]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectedReq = MOCK_REQUISITIONS.find(r => r.id === form.requisitionId);

  // Auto-populate items from requisition
  const handleReqChange = (reqId: string) => {
    setForm(f => ({ ...f, requisitionId: reqId }));
    const req = MOCK_REQUISITIONS.find(r => r.id === reqId);
    if (req) {
      setItems(req.items.map((item, i) => ({
        id: `new-${i + 1}`,
        description: item.description,
        specifications: item.specifications || '',
        category: item.category,
        unit: item.unit,
        quantity: item.quantity,
        requiredDeliveryDate: req.requiredByDate,
      })));
    }
  };

  const toggleSupplier = (id: string) => {
    setSelectedSuppliers(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const addItem = () => {
    setItems(prev => [...prev, { id: `new-${Date.now()}`, description: '', specifications: '', category: 'Feed', unit: 'bag', quantity: 1 }]);
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const updateItem = (id: string, field: keyof RFQItem, value: string | number) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.submissionDeadline) e.submissionDeadline = 'Submission deadline is required';
    if (selectedSuppliers.length === 0) e.suppliers = 'Select at least one supplier';
    if (items.some(i => !i.description.trim())) e.items = 'All items must have a description';
    if (items.some(i => i.quantity <= 0)) e.itemsQty = 'All quantities must be greater than zero';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = (asDraft: boolean) => {
    if (!asDraft && !validate()) return;
    const rfqNum = `RFQ-2026-${String(MOCK_RFQS.length + 14).padStart(4, '0')}`;
    const newRFQ: RFQ = {
      id: `rfq-${Date.now()}`,
      rfqNumber: rfqNum,
      rfqDate: new Date().toISOString().split('T')[0],
      submissionDeadline: form.submissionDeadline,
      farm: selectedReq?.farm || 'Idudi Poultry Farm',
      requisitionId: form.requisitionId || undefined,
      requisitionNumber: selectedReq?.requisitionNumber,
      procurementOfficer: 'Mugisha Robert',
      currency: form.currency,
      deliveryLocation: form.deliveryLocation,
      paymentTerms: form.paymentTerms,
      deliveryTerms: form.deliveryTerms,
      notes: form.notes,
      status: asDraft ? 'draft' : 'issued',
      suppliers: selectedSuppliers.map(sid => {
        const sup = SUPPLIERS.find(s => s.id === sid)!;
        return { supplierId: sid, supplierName: sup.name, supplierCode: sup.code, sentDate: asDraft ? undefined : new Date().toISOString().split('T')[0], responseStatus: 'pending' };
      }),
      items,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onSave(newRFQ);
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-base font-bold text-gray-900">Create Request for Quotation</div>
            <div className="text-xs text-gray-400 mt-0.5">Issue RFQ to suppliers for pricing and delivery terms</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-6">
          {/* Link to Requisition */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Link to Approved Requisition <span className="text-gray-400 font-normal">(optional)</span></label>
            <select
              value={form.requisitionId}
              onChange={e => handleReqChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
            >
              <option value="">— Select Requisition —</option>
              {approvedReqs.map(r => (
                <option key={r.id} value={r.id}>{r.requisitionNumber} · {r.department} · {formatUGX(r.estimatedTotal)}</option>
              ))}
            </select>
            {selectedReq && (
              <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-xl text-xs text-green-700">
                <strong>{selectedReq.requisitionNumber}</strong> — {selectedReq.purpose} · Items auto-populated below.
              </div>
            )}
          </div>

          {/* RFQ Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Submission Deadline <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={form.submissionDeadline}
                onChange={e => setForm(f => ({ ...f, submissionDeadline: e.target.value }))}
                className={`w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 ${errors.submissionDeadline ? 'border-red-400' : 'border-gray-200'}`}
              />
              {errors.submissionDeadline && <p className="text-xs text-red-500 mt-1">{errors.submissionDeadline}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Currency</label>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
                <option>UGX</option><option>USD</option><option>KES</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Payment Terms</label>
              <select value={form.paymentTerms} onChange={e => setForm(f => ({ ...f, paymentTerms: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
                {['Net 14', 'Net 30', 'Net 60', 'Cash on Delivery', 'Advance Payment', '50% Advance'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Delivery Terms</label>
              <select value={form.deliveryTerms} onChange={e => setForm(f => ({ ...f, deliveryTerms: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
                {['DDP', 'EXW', 'CIF', 'FOB', 'DAP'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Delivery Location</label>
            <input
              type="text"
              value={form.deliveryLocation}
              onChange={e => setForm(f => ({ ...f, deliveryLocation: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          {/* Supplier Selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Invite Suppliers <span className="text-red-500">*</span>
              <span className="ml-1 text-gray-400 font-normal">({selectedSuppliers.length} selected)</span>
            </label>
            {errors.suppliers && <p className="text-xs text-red-500 mb-2">{errors.suppliers}</p>}
            <div className="space-y-2">
              {SUPPLIERS.filter(s => s.status === 'active').map(sup => (
                <label key={sup.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedSuppliers.includes(sup.id) ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input
                    type="checkbox"
                    checked={selectedSuppliers.includes(sup.id)}
                    onChange={() => toggleSupplier(sup.id)}
                    className="w-4 h-4 accent-orange-600"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-800">{sup.name}</div>
                    <div className="text-xs text-gray-400">{sup.code} · {sup.category}</div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-amber-600 font-semibold">
                    ★ {sup.rating}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700">RFQ Items <span className="text-red-500">*</span></label>
              <button onClick={addItem} className="flex items-center gap-1 text-xs text-orange-600 font-semibold hover:text-orange-700">
                <Plus size={12} /> Add Item
              </button>
            </div>
            {(errors.items || errors.itemsQty) && <p className="text-xs text-red-500 mb-2">{errors.items || errors.itemsQty}</p>}
            <div className="space-y-3">
              {items.map((item, idx) => (
                <div key={item.id} className="border border-gray-200 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-500">Item {idx + 1}</span>
                    {items.length > 1 && (
                      <button onClick={() => removeItem(item.id)} className="text-red-400 hover:text-red-600">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Item description *"
                    value={item.description}
                    onChange={e => updateItem(item.id, 'description', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <input
                    type="text"
                    placeholder="Specifications (optional)"
                    value={item.specifications || ''}
                    onChange={e => updateItem(item.id, 'specifications', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <select value={item.category} onChange={e => updateItem(item.id, 'category', e.target.value)} className="px-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
                      {PROCUREMENT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                    <select value={item.unit} onChange={e => updateItem(item.id, 'unit', e.target.value)} className="px-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
                      {UNITS.map(u => <option key={u}>{u}</option>)}
                    </select>
                    <input
                      type="number"
                      placeholder="Qty"
                      min={1}
                      value={item.quantity}
                      onChange={e => updateItem(item.id, 'quantity', Number(e.target.value))}
                      className="px-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                  <input
                    type="date"
                    placeholder="Required delivery date"
                    value={item.requiredDeliveryDate || ''}
                    onChange={e => updateItem(item.id, 'requiredDeliveryDate', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Notes / Instructions to Suppliers</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Special instructions, quality requirements, etc."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
          <button onClick={() => handleSave(false)} className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors">
            <Send size={14} /> Issue RFQ
          </button>
          <button onClick={() => handleSave(true)} className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
            Save as Draft
          </button>
          <button onClick={onClose} className="ml-auto px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function RFQsContent() {
  const { can } = useRole();
  const canManage = can('manage_procurement');
  const canView = can('view_procurement');

  const [rfqs, setRfqs] = useState<RFQ[]>(MOCK_RFQS);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<RFQStatus | 'all'>('all');
  const [selectedRFQ, setSelectedRFQ] = useState<RFQ | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);

  const filtered = useMemo(() => {
    return rfqs.filter(r => {
      const matchSearch = r.rfqNumber.toLowerCase().includes(search.toLowerCase()) ||
        r.farm.toLowerCase().includes(search.toLowerCase()) ||
        r.procurementOfficer.toLowerCase().includes(search.toLowerCase()) ||
        (r.requisitionNumber || '').toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || r.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [rfqs, search, statusFilter]);

  const handleIssue = (id: string) => {
    setRfqs(prev => prev.map(r => r.id === id ? {
      ...r,
      status: 'issued' as RFQStatus,
      suppliers: r.suppliers.map(s => ({ ...s, sentDate: new Date().toISOString().split('T')[0] })),
    } : r));
    setSelectedRFQ(prev => prev?.id === id ? { ...prev, status: 'issued' as RFQStatus } : prev);
  };

  const handleSaveNew = (rfq: RFQ) => {
    setRfqs(prev => [rfq, ...prev]);
    setShowNewForm(false);
  };

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: rfqs.length };
    rfqs.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [rfqs]);

  if (!canView) {
    return (
      <div className="p-8 text-center">
        <FileSearch size={48} className="mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Access Restricted</h2>
        <p className="text-gray-500">You do not have permission to view RFQs.</p>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-screen-2xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link href="/procurement" className="text-gray-400 hover:text-gray-600 transition-colors">
              <ChevronRight size={16} className="rotate-180" />
            </Link>
            <FileSearch size={22} className="text-orange-600" />
            <h1 className="text-2xl font-bold text-gray-900">Requests for Quotation</h1>
          </div>
          <p className="text-sm text-gray-500 max-w-xl">
            Create and manage RFQs from approved requisitions. Invite suppliers, track responses, and proceed to quotation comparison.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            <Download size={14} /> Export
          </button>
          {canManage && (
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors shadow-sm"
            >
              <Plus size={15} /> Create RFQ
            </button>
          )}
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {([
          { label: 'Total RFQs', key: 'all', color: 'text-gray-700', bg: 'bg-gray-50' },
          { label: 'Draft', key: 'draft', color: 'text-gray-600', bg: 'bg-gray-100' },
          { label: 'Issued', key: 'issued', color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Open', key: 'open', color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Closed', key: 'closed', color: 'text-amber-700', bg: 'bg-amber-50' },
          { label: 'Evaluated', key: 'evaluated', color: 'text-purple-700', bg: 'bg-purple-50' },
        ] as const).map(s => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key as RFQStatus | 'all')}
            className={`p-3 rounded-2xl border text-left transition-all ${statusFilter === s.key ? `${s.bg} border-current ring-2 ring-offset-1 ring-orange-400` : 'bg-white border-gray-100 hover:border-gray-200'}`}
          >
            <div className={`text-2xl font-bold ${s.color}`}>{statusCounts[s.key] || 0}</div>
            <div className="text-xs text-gray-500 mt-0.5 font-medium">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by RFQ number, farm, officer, requisition..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as RFQStatus | 'all')}
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
          >
            <option value="all">All Statuses</option>
            {(['draft', 'issued', 'open', 'closed', 'evaluated', 'cancelled'] as RFQStatus[]).map(s => (
              <option key={s} value={s}>{RFQ_STATUS_STYLES[s].label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <FileSearch size={40} className="mx-auto text-gray-300 mb-3" />
            <div className="text-base font-semibold text-gray-600 mb-1">No RFQs found</div>
            <div className="text-sm text-gray-400">
              {search || statusFilter !== 'all' ? 'Try adjusting your search or filters.' : 'Create your first RFQ from an approved requisition.'}
            </div>
            {canManage && !search && statusFilter === 'all' && (
              <button onClick={() => setShowNewForm(true)} className="mt-4 flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors mx-auto">
                <Plus size={14} /> Create RFQ
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">RFQ Number</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Requisition</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Farm</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Deadline</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Suppliers</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Items</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(rfq => {
                  const respondedCount = rfq.suppliers.filter(s => s.responseStatus === 'responded').length;
                  const isDeadlineSoon = rfq.status === 'open' && new Date(rfq.submissionDeadline) <= new Date(Date.now() + 3 * 86400000);
                  return (
                    <tr key={rfq.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{rfq.rfqNumber}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{rfq.rfqDate}</div>
                      </td>
                      <td className="px-4 py-3">
                        {rfq.requisitionNumber ? (
                          <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{rfq.requisitionNumber}</span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{rfq.farm}</td>
                      <td className="px-4 py-3">
                        <div className={`text-sm font-medium ${isDeadlineSoon ? 'text-red-600' : 'text-gray-700'}`}>
                          {rfq.submissionDeadline}
                        </div>
                        {isDeadlineSoon && <div className="text-xs text-red-500 mt-0.5 flex items-center gap-1"><AlertTriangle size={10} /> Deadline soon</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700">{rfq.suppliers.length} invited</div>
                        <div className="text-xs text-gray-400">{respondedCount} responded</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{rfq.items.length} item{rfq.items.length !== 1 ? 's' : ''}</span>
                      </td>
                      <td className="px-4 py-3">
                        <RFQStatusBadge status={rfq.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setSelectedRFQ(rfq)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                            title="View Details"
                          >
                            <Eye size={14} />
                          </button>
                          {canManage && rfq.status === 'draft' && (
                            <button
                              onClick={() => handleIssue(rfq.id)}
                              className="p-1.5 rounded-lg hover:bg-orange-50 transition-colors text-orange-500 hover:text-orange-700"
                              title="Issue RFQ"
                            >
                              <Send size={14} />
                            </button>
                          )}
                          <Link
                            href={`/procurement/quotations?rfq=${rfq.id}`}
                            className="p-1.5 rounded-lg hover:bg-indigo-50 transition-colors text-indigo-500 hover:text-indigo-700"
                            title="View Quotations"
                          >
                            <FileSearch size={14} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination hint */}
      {filtered.length > 0 && (
        <div className="mt-3 text-xs text-gray-400 text-right">
          Showing {filtered.length} of {rfqs.length} RFQ{rfqs.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Detail Drawer */}
      {selectedRFQ && (
        <RFQDrawer
          rfq={selectedRFQ}
          onClose={() => setSelectedRFQ(null)}
          onIssue={handleIssue}
        />
      )}

      {/* New RFQ Form */}
      {showNewForm && (
        <NewRFQForm
          onClose={() => setShowNewForm(false)}
          onSave={handleSaveNew}
        />
      )}
    </div>
  );
}
